import { hydrateProducts } from '../data/products'
import { hydrateCategories } from '../data/categories'
import { hydrateConfig } from '../config'
import { api } from './api'
import type { Bootstrap, Product } from '../types'

const BOOTSTRAP_TIMEOUT_MS = 6000

/**
 * Loads the catalogue and the settings before the app mounts, so the modules
 * that import PRODUCTS, CATEGORIES and the config constants read server data on
 * their first render and none of them need to know about the API.
 *
 * On failure the app still starts on the catalogue baked in at build time. That
 * is a real degraded mode, not a theoretical one: D1's free tier now fails
 * queries outright once the daily row limit is reached, and a catalogue that is
 * a little stale beats one that shows nothing.
 */
/**
 * A product from a catalogue served before sizes existed (migration 0010) has no
 * `variants`. Every component reads them, so one stale response took the whole
 * site down. Give such a product its single size back instead.
 */
function withVariants(product: Product): Product {
  if (Array.isArray(product.variants) && product.variants.length > 0) return product
  return { ...product, variants: [{ id: product.id, label: { sq: '', en: '' } }] }
}

export async function loadBootstrap(): Promise<{ live: boolean; error?: string }> {
  try {
    // Nothing renders until this settles, so a hung request must not hang the
    // site: give up and use the bundled catalogue instead.
    // 'no-cache': always ask the server, even if the browser holds a copy. Cloudflare
    // has handed this response to browsers with a 4-hour lifetime before, and a
    // catalogue that old can predate the code reading it.
    const data = await api<Bootstrap>('/bootstrap', {
      signal: AbortSignal.timeout(BOOTSTRAP_TIMEOUT_MS),
      cache: 'no-cache',
    })
    hydrateProducts(data.products.map(withVariants))
    hydrateCategories(data.categories)
    hydrateConfig(data.config)
    return { live: true }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    console.warn('[3ac] bootstrap failed, using the catalogue built into this bundle:', message)
    return { live: false, error: message }
  }
}
